import { useForm, SubmitHandler, Controller } from "react-hook-form"
import Button from "@/components/Button"
import { GameShowProps } from "@/types/types"
import formatDate from "@/utils/formatDate"
import { Modals, useModals } from "@/hooks/useModals"
import useUser from "@/hooks/useUser"
import Modal from "@/components/Modal"
import PlayerSelector from "@/components/game/PlayerSelector"
import EventSelector from "@/components/game/EventSelector"
import { router, usePage } from "@inertiajs/react"
import GameResult from "@/types/GameResult"
import { useState } from "react"
import ErrorMessage from "@/components/ErrorMessage"
import { captureEvent } from "@/lib/posthog"
import axios from "axios"

export const resultOptions: GameResult[] = ["*", "1-0", "0-1", "1/2-1/2"]

type Inputs = {
  title?: string
  date?: string
  result?: GameResult
  event_id?: string
  event_round?: string
  board?: string
  white_player_id?: string
  white_elo?: string
  black_player_id?: string
  black_elo?: string
  description?: string
}

const today = formatDate({ date: new Date() })

const SaveGameModal = () => {
  const { game: savedGame } = usePage<GameShowProps>().props
  const { activeModal, closeModal } = useModals()
  const { user } = useUser()
  const isOpen = activeModal === Modals.SaveGame
  const { id } = savedGame

  const [processing, setProcessing] = useState(false)
  const [isWhiteMaterializing, setIsWhiteMaterializing] = useState(false)
  const [isBlackMaterializing, setIsBlackMaterializing] = useState(false)

  // "Me" chip: shown on empty fields when user has a linked player.
  // Dismissed permanently once used or a player is selected on either field.
  const hasLinkedPlayer = Boolean(user?.player_id)
  const bothFieldsEmptyOnOpen = !savedGame?.white_player && !savedGame?.black_player
  const [meChipDismissed, setMeChipDismissed] = useState(!bothFieldsEmptyOnOpen || !hasLinkedPlayer)

  // "Is this you?" discovery: shown when user has NO linked player.
  // Dismissed permanently once used or dismissed on either field.
  const [isThisYouDismissed, setIsThisYouDismissed] = useState(hasLinkedPlayer)
  const [isThisYouPlayerId, setIsThisYouPlayerId] = useState<string | null>(null)
  const [settingMyPlayer, setSettingMyPlayer] = useState(false)

  const handleIsThisYouClick = (playerId: string) => {
    setIsThisYouPlayerId(playerId)
  }

  const handleIsThisYouConfirm = async () => {
    if (!isThisYouPlayerId) return
    try {
      setSettingMyPlayer(true)
      await axios.patch("/settings.json", { setting: { player_id: isThisYouPlayerId } })
      captureEvent("my_player_set", { source: "is_this_you" })
      setIsThisYouDismissed(true)
      setIsThisYouPlayerId(null)
      router.reload({ only: ["auth"] })
    } catch {
      // Silently fail — player_id not set, but game save still works
      setIsThisYouPlayerId(null)
      setIsThisYouDismissed(true)
    } finally {
      setSettingMyPlayer(false)
    }
  }

  const handleIsThisYouCancel = () => {
    setIsThisYouPlayerId(null)
    setIsThisYouDismissed(true)
  }

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      title: savedGame?.title ?? undefined,
      date: savedGame?.date ?? today,
      result: savedGame?.result ?? "*",
      event_id: savedGame?.event?.id ?? undefined,
      event_round: savedGame?.event_round?.toString(),
      board: savedGame?.board?.toString(),
      white_player_id: savedGame?.white_player?.id ?? undefined,
      white_elo: savedGame?.white_elo?.toString(),
      black_player_id: savedGame?.black_player?.id ?? undefined,
      black_elo: savedGame?.black_elo?.toString(),
      description: savedGame?.description ?? undefined,
    },
  })

  const saveGame: SubmitHandler<Inputs> = async (data) => {
    try {
      router.patch(
        `/games/${id}`,
        { game: { ...data, draft: false } },
        {
          async: true,
          preserveScroll: true,
          preserveState: true,
          onSuccess: () => closeModal(),
          onStart: () => setProcessing(true),
          onFinish: () => setProcessing(false),
        }
      )
    } catch (error: any) {
      console.log(error)
      setError("root.serverError", {
        message: error.message,
      })
    }
  }

  return (
    <>
    <Modal
      show={isOpen}
      onClose={closeModal}
      title={savedGame?.draft ? "Save game (all fields optional)" : "Edit game"}
    >
      <form onSubmit={handleSubmit(saveGame)} className="mt-4 space-y-5">

        <div className="space-y-4">
          {savedGame?.chess960 && (
            <div className="flex items-center gap-x-2">
              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200 ring-inset">
                Freestyle
              </span>
            </div>
          )}

          <div className="max-w-40">
            <label
              htmlFor="date"
              className="block text-sm leading-6 font-medium text-gray-900"
            >
              Date
            </label>
            <input
              type="date"
              id="date"
              className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
              max={today}
              {...register("date")}
            />
          </div>

          <Controller
            name="event_id"
            control={control}
            render={({ field: { onChange, value, onBlur } }) => (
              <EventSelector
                eventId={value}
                selectedEventFallback={savedGame?.event}
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />

          <div className="grid grid-cols-3 gap-x-2">
            <div>
              <label
                htmlFor="event_round"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Round
              </label>
              <input
                type="number"
                inputMode="numeric"
                id="event_round"
                className="focus:ring-chessdrop-600 hide-spinners mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                {...register("event_round", {
                  min: {
                    value: 0,
                    message: "Round should be a positive number.",
                  },
                })}
              />
              <ErrorMessage
                className="mt-1.5"
                message={errors.event_round?.message}
              />
            </div>

            <div>
              <label
                htmlFor="board"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Board
              </label>
              <input
                type="number"
                inputMode="numeric"
                id="board"
                className="focus:ring-chessdrop-600 hide-spinners mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                {...register("board", {
                  min: {
                    value: 0,
                    message: "Board should be a positive number.",
                  },
                })}
              />
              <ErrorMessage
                className="mt-1.5"
                message={errors.board?.message}
              />
            </div>

            <div>
              <label
                htmlFor="result"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Result
              </label>
              <select
                id="result"
                className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                {...register("result")}
              >
                {resultOptions.map((result) => (
                  <option key={result} value={result}>
                    {result.replace("1/2-1/2", "½-½")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-5">
          <h3 className="text-sm font-semibold text-gray-900">Players</h3>
        </div>

        <div className="space-y-4">
          <div className="grid w-full grid-cols-4 gap-x-2">
            <Controller
              name="white_player_id"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <PlayerSelector
                  playerId={value}
                  selectedPlayerFallback={savedGame?.white_player}
                  onChange={(id: string | null) => {
                    onChange(id)
                    if (id) setMeChipDismissed(true)
                  }}
                  onBlur={onBlur}
                  onMaterializingChange={setIsWhiteMaterializing}
                  label="White"
                  showMeChip={!meChipDismissed}
                  onMeChipUsed={() => { setMeChipDismissed(true); captureEvent("me_chip_used") }}
                  showIsThisYou={!isThisYouDismissed}
                  onIsThisYouClick={handleIsThisYouClick}
                />
              )}
            />
            <div className="col-span-1">
              <label
                htmlFor="white_elo"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Elo
              </label>
              <input
                type="number"
                inputMode="numeric"
                id="white_elo"
                className="hide-spinners focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                {...register("white_elo", {
                  min: {
                    value: 0,
                    message: "Elo should be a positive number.",
                  },
                })}
              />
            </div>

            <ErrorMessage
              className="col-span-4 mt-1.5 text-right"
              message={errors.white_elo?.message}
            />
          </div>

          <div className="grid w-full grid-cols-4 gap-x-2">
            <Controller
              name="black_player_id"
              control={control}
              render={({ field: { onChange, value, onBlur } }) => (
                <PlayerSelector
                  playerId={value}
                  selectedPlayerFallback={savedGame?.black_player}
                  onChange={(id: string | null) => {
                    onChange(id)
                    if (id) setMeChipDismissed(true)
                  }}
                  onBlur={onBlur}
                  onMaterializingChange={setIsBlackMaterializing}
                  label="Black"
                  maxHeightClassName="max-h-36"
                  showMeChip={!meChipDismissed}
                  onMeChipUsed={() => { setMeChipDismissed(true); captureEvent("me_chip_used") }}
                  showIsThisYou={!isThisYouDismissed}
                  onIsThisYouClick={handleIsThisYouClick}
                />
              )}
            />
            <div className="col-span-1">
              <label
                htmlFor="black_elo"
                className="block text-sm leading-6 font-medium text-gray-900"
              >
                Elo
              </label>
              <input
                type="number"
                inputMode="numeric"
                id="black_elo"
                className="hide-spinners focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                {...register("black_elo", {
                  min: {
                    value: 0,
                    message: "Elo should be a positive number.",
                  },
                })}
              />
            </div>

            <ErrorMessage
              className="col-span-4 mt-1.5 text-right"
              message={errors.black_elo?.message}
            />
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="title"
              className="block text-sm leading-6 font-medium text-gray-900"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
              {...register("title")}
            />
          </div>

          <div>
            <label
              htmlFor="description"
              className="block text-sm leading-6 font-medium text-gray-900"
            >
              Description
            </label>
            <textarea
              rows={3}
              id="description"
              className="focus:ring-chessdrop-600 mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
              {...register("description")}
            />
          </div>
        </div>

        <div className="border-t border-gray-200 pt-5">
          <Button loading={processing || isWhiteMaterializing || isBlackMaterializing} type="submit" fullWidth>
            Save game
          </Button>

          {errors?.root?.serverError ? (
            <p className="mt-3 text-center text-sm font-medium text-red-600">
              {errors.root.serverError.message}
            </p>
          ) : null}
        </div>
      </form>
    </Modal>

    <Modal
      show={isThisYouPlayerId !== null}
      onClose={handleIsThisYouCancel}
      title="Is this you?"
      maxWidthClassName="sm:max-w-sm"
    >
      <div className="mt-4 space-y-4">
        <p className="text-sm text-gray-600">
          Set this player as yourself and it will always appear first when
          selecting players, making it faster to save your games.
        </p>
        <p className="text-sm text-gray-400">
          You can change this anytime in Settings.
        </p>
        <div className="flex gap-x-3">
          <Button
            type="button"
            fullWidth
            loading={settingMyPlayer}
            onClick={handleIsThisYouConfirm}
          >
            Yes, this is me
          </Button>
          <Button
            type="button"
            style="secondary"
            fullWidth
            onClick={handleIsThisYouCancel}
            disabled={settingMyPlayer}
          >
            No
          </Button>
        </div>
      </div>
    </Modal>
    </>
  )
}

export default SaveGameModal
